import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50 shadow-sm' : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6" aria-label="主导航">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="font-heading text-2xl font-bold text-primary-500">奇妙漫画</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/' ? 'text-primary-500' : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              {t('nav.home')}
            </Link>

            <div className="relative" onMouseEnter={() => setCategoryOpen(true)} onMouseLeave={() => setCategoryOpen(false)}>
              <Link
                to="/category/hanman"
                className={`text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${
                  location.pathname.startsWith('/category') ? 'text-primary-500' : 'text-foreground-700 hover:text-primary-500'
                }`}
              >
                {t('nav.category')}
                <i className="ri-arrow-down-s-line text-xs"></i>
              </Link>
              {categoryOpen && (
                <div className="absolute top-full left-0 mt-2 bg-background-50 rounded-lg border border-background-200 py-2 min-w-[160px]">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/about' ? 'text-primary-500' : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              {t('nav.about')}
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder={t('nav.search')}
                className="w-48 pl-9 pr-3 py-2 bg-background-100 border border-background-200 rounded-full text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            </div>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="切换菜单"
          >
            <i className={`text-xl text-foreground-700 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4">
          <div className="flex flex-col gap-3">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap" onClick={() => setMobileOpen(false)}>
              {t('nav.home')}
            </Link>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-foreground-500">{t('nav.category')}</span>
              <div className="grid grid-cols-2 gap-1 pl-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
                    onClick={() => setMobileOpen(false)}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
            <Link to="/about" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap" onClick={() => setMobileOpen(false)}>
              {t('nav.about')}
            </Link>
            <div className="relative mt-2">
              <input
                type="text"
                placeholder={t('nav.search')}
                className="w-full pl-9 pr-3 py-2 bg-background-100 border border-background-200 rounded-full text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}